import React, { useState, useEffect } from 'react';
import { plantAPI, departmentAPI, shiftAPI, machineAPI } from '../../services/api';
import { Plant, PlantCreate, Department, DepartmentCreate, Shift, ShiftCreate } from '../../types/operations';
import { MachineWithShifts } from '../../types/machine';
import MachineModal from '../MachineModal';

const OperationsTab: React.FC = () => {
  // State for operations data
  const [plants, setPlants] = useState<Plant[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [machines, setMachines] = useState<MachineWithShifts[]>([]);

  // Selection state
  const [selectedPlantId, setSelectedPlantId] = useState<number>(0);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for creating new entities
  const [newPlantName, setNewPlantName] = useState('');
  const [showPlantForm, setShowPlantForm] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [newShiftName, setNewShiftName] = useState('');
  const [showShiftForm, setShowShiftForm] = useState(false);

  // Machine modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<MachineWithShifts | null>(null);

  // Load plants on mount
  useEffect(() => {
    loadPlants();
  }, []);

  // Auto-select first plant when plants load
  useEffect(() => {
    if (plants.length > 0 && selectedPlantId === 0) {
      setSelectedPlantId(plants[0].id);
    }
  }, [plants]);

  // Load departments when plant changes
  useEffect(() => {
    if (selectedPlantId > 0) {
      loadDepartments(selectedPlantId);
    } else {
      setDepartments([]);
      setSelectedDepartmentId(0);
    }
  }, [selectedPlantId]);

  // Auto-select first department when departments load
  useEffect(() => {
    if (departments.length > 0 && selectedDepartmentId === 0) {
      setSelectedDepartmentId(departments[0].id);
    }
  }, [departments]);

  // Load shifts and machines when department changes
  useEffect(() => {
    if (selectedDepartmentId > 0) {
      loadShifts(selectedDepartmentId);
      loadMachines(selectedDepartmentId);
    } else {
      setShifts([]);
      setMachines([]);
    }
  }, [selectedDepartmentId]);

  const loadPlants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await plantAPI.list();
      setPlants(data);
    } catch (err: any) {
      setError(`Failed to load plants: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async (plantId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await departmentAPI.list(plantId);
      setDepartments(data);
    } catch (err: any) {
      setError(`Failed to load departments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadShifts = async (departmentId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await shiftAPI.list(departmentId);
      setShifts(data);
    } catch (err: any) {
      setError(`Failed to load shifts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async (departmentId: number) => {
    try {
      setLoading(true);
      setError(null);
      const allMachines = await machineAPI.list();
      const filtered = allMachines.filter((m) => m.department_id === departmentId);
      setMachines(filtered);
    } catch (err: any) {
      setError(`Failed to load machines: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create handlers
  const handleCreatePlant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPlantName.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const plantData: PlantCreate = {
        name: newPlantName.trim(),
        is_active: true,
      };
      await plantAPI.create(plantData);
      setNewPlantName('');
      setShowPlantForm(false);
      await loadPlants();
    } catch (err: any) {
      setError(`Failed to create plant: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDepartmentName.trim() || selectedPlantId === 0) return;

    try {
      setLoading(true);
      setError(null);
      const departmentData: DepartmentCreate = {
        name: newDepartmentName.trim(),
        plant_id: selectedPlantId,
        is_active: true,
      };
      await departmentAPI.create(departmentData);
      setNewDepartmentName('');
      setShowDepartmentForm(false);
      await loadDepartments(selectedPlantId);
    } catch (err: any) {
      setError(`Failed to create department: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShift = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newShiftName.trim() || selectedDepartmentId === 0) return;

    try {
      setLoading(true);
      setError(null);
      const shiftData: ShiftCreate = {
        name: newShiftName.trim(),
        department_id: selectedDepartmentId,
        is_active: true,
      };
      await shiftAPI.create(shiftData);
      setNewShiftName('');
      setShowShiftForm(false);
      await loadShifts(selectedDepartmentId);
    } catch (err: any) {
      setError(`Failed to create shift: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Machine handlers
  const handleCreateMachine = () => {
    setEditingMachine(null);
    setIsModalOpen(true);
  };

  const handleEditMachine = (machine: MachineWithShifts) => {
    setEditingMachine(machine);
    setIsModalOpen(true);
  };

  const handleDeleteMachine = async (machineId: number) => {
    if (!window.confirm('Are you sure you want to delete this machine?')) return;

    try {
      setLoading(true);
      setError(null);
      await machineAPI.delete(machineId);
      await loadMachines(selectedDepartmentId);
    } catch (err: any) {
      setError(`Failed to delete machine: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShift = async (shiftId: number) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;

    try {
      setLoading(true);
      setError(null);
      await shiftAPI.delete(shiftId);
      await loadShifts(selectedDepartmentId);
    } catch (err: any) {
      setError(`Failed to delete shift: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingMachine(null);
  };

  const handleModalSubmit = async (machineData: any) => {
    try {
      if (editingMachine) {
        await machineAPI.update(editingMachine.id, machineData);
      } else {
        await machineAPI.create(machineData);
      }
      setIsModalOpen(false);
      setEditingMachine(null);
      await loadMachines(selectedDepartmentId);
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg shadow p-4" style={{ background: 'var(--bg-secondary)' }}>
        <h2 className="text-base font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          Operations Management
        </h2>

        {error && (
          <div className="mb-3 p-2 rounded text-sm" style={{ backgroundColor: '#fee', color: '#c00' }}>
            {error}
          </div>
        )}

        {/* Compact Horizontal Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {/* Plant Selector */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              Plant
            </label>
            <div className="flex gap-2">
              <select
                value={selectedPlantId}
                onChange={(e) => setSelectedPlantId(parseInt(e.target.value))}
                className="flex-1 px-3 py-2 border rounded-md text-sm"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <option value={0}>-- Select Plant --</option>
                {plants.map((plant) => (
                  <option key={plant.id} value={plant.id}>
                    {plant.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowPlantForm(!showPlantForm)}
                className="px-3 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                title="Add new plant"
              >
                +
              </button>
            </div>
            {showPlantForm && (
              <form onSubmit={handleCreatePlant} className="mt-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPlantName}
                    onChange={(e) => setNewPlantName(e.target.value)}
                    placeholder="Plant name"
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)',
                    }}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-2 py-1 rounded text-sm bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPlantForm(false); setNewPlantName(''); }}
                    className="px-2 py-1 rounded text-sm bg-gray-400 text-white hover:bg-gray-500"
                  >
                    ✕
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Department Selector */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              Department
            </label>
            <div className="flex gap-2">
              <select
                value={selectedDepartmentId}
                onChange={(e) => setSelectedDepartmentId(parseInt(e.target.value))}
                disabled={selectedPlantId === 0}
                className="flex-1 px-3 py-2 border rounded-md text-sm disabled:opacity-50"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <option value={0}>-- Select Department --</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowDepartmentForm(!showDepartmentForm)}
                disabled={selectedPlantId === 0}
                className="px-3 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                title="Add new department"
              >
                +
              </button>
            </div>
            {showDepartmentForm && selectedPlantId > 0 && (
              <form onSubmit={handleCreateDepartment} className="mt-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDepartmentName}
                    onChange={(e) => setNewDepartmentName(e.target.value)}
                    placeholder="Department name"
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)',
                    }}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-2 py-1 rounded text-sm bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowDepartmentForm(false); setNewDepartmentName(''); }}
                    className="px-2 py-1 rounded text-sm bg-gray-400 text-white hover:bg-gray-500"
                  >
                    ✕
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Shifts Management Section - Only show when department is selected */}
        {selectedDepartmentId > 0 && (
          <div className="mb-3 p-3 border rounded" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-primary)' }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Shifts
              </h3>
              <button
                onClick={() => setShowShiftForm(!showShiftForm)}
                className="px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                + Add
              </button>
            </div>

            {showShiftForm && (
              <form onSubmit={handleCreateShift} className="mb-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newShiftName}
                    onChange={(e) => setNewShiftName(e.target.value)}
                    placeholder="Shift name (e.g., Morning, Evening, Night)"
                    className="flex-1 px-2 py-1 border rounded text-sm"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)',
                    }}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-2 py-1 rounded text-sm bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowShiftForm(false); setNewShiftName(''); }}
                    className="px-2 py-1 rounded text-sm bg-gray-400 text-white hover:bg-gray-500"
                  >
                    ✕
                  </button>
                </div>
              </form>
            )}

            {shifts.length === 0 ? (
              <p className="text-xs text-center py-2" style={{ color: 'var(--text-secondary)' }}>
                No shifts configured. Click "+ Add" to create one.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {shifts.map((shift) => (
                  <div
                    key={shift.id}
                    className="p-2 rounded border flex items-center justify-between"
                    style={{
                      background: 'var(--bg-secondary)',
                      borderColor: 'var(--border-color)',
                    }}
                  >
                    <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                      {shift.name}
                    </span>
                    <button
                      onClick={() => handleDeleteShift(shift.id)}
                      className="text-red-600 hover:text-red-800 text-xs px-1"
                      title="Delete shift"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Machines Section - Primary Focus */}
      {selectedDepartmentId > 0 && (
        <div className="rounded-lg shadow p-4" style={{ background: 'var(--bg-secondary)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              Machines
            </h3>
            <button
              onClick={handleCreateMachine}
              className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
            >
              + Add Machine
            </button>
          </div>

          {machines.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
              <div className="text-3xl mb-2">🏭</div>
              <p className="text-sm mb-1 font-medium">No machines configured yet</p>
              <p className="text-xs">Click "Add Machine" to add your first machine to this department</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th className="text-left p-2 text-sm" style={{ color: 'var(--text-primary)' }}>Machine ID</th>
                    <th className="text-left p-2 text-sm" style={{ color: 'var(--text-primary)' }}>Machine Name</th>
                    <th className="text-center p-2 text-sm" style={{ color: 'var(--text-primary)' }}>Status</th>
                    <th className="text-right p-2 text-sm" style={{ color: 'var(--text-primary)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {machines.map((machine) => (
                    <tr
                      key={machine.id}
                      className="border-b"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <td className="p-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                        <span className="font-mono font-medium">{machine.machine_id}</span>
                      </td>
                      <td className="p-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                        {machine.machine_name}
                      </td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${machine.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {machine.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => handleEditMachine(machine)}
                          className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 mr-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteMachine(machine.id)}
                          className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Machine Modal */}
      <MachineModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        machine={editingMachine}
        mode={editingMachine ? 'edit' : 'create'}
        selectedPlantId={selectedPlantId}
        selectedDepartmentId={selectedDepartmentId}
      />
    </div>
  );
};

export default OperationsTab;
